import { BarcodeType } from '@/models/Ean'
import { ProductInput } from '@/models/Import'
import { ProductPropertyStatus, SimplifiedProduct } from '@/models/Product'
import { AdvancedProperty, PartialProperty, PropertyValueType } from '@/models/Property'
import { RegisteredTaxonomy } from '@/models/Taxonomy'
import { toComparable } from './tools'

export const isSkuProperty = (property: PartialProperty) => property.defaultName === 'Item no'
export const isAvailableStatusProperty = (property: PartialProperty) => property.defaultName === 'Availability status'
export const isInfoTextProperty = (property: PartialProperty) => property.defaultName === 'Info text'
export const isMainNameProperty = (property: PartialProperty) => property.defaultName === 'Main name'
export const isBarcodeProperty = (property: PartialProperty) => property.defaultName.toLowerCase().includes('barcode')

export const propertyHasStaticValues = (property: AdvancedProperty) =>
  !property?.hasTerms && property.type !== 'categories' && property.type !== 'simpleTaxonomy' && !property.computedOptions

export const isMultiLineProperty = (property: AdvancedProperty) => !!(property.type === 'categories' || property.allowMultipleValues || property.multiLine)

export const shouldUseSkuComponent = (property: PartialProperty) => property.defaultName.toLowerCase().includes('item no')

export const propertyCanGenerateEan = (property: AdvancedProperty) => property.defaultName.toLowerCase().includes('barcode')

export const valueTypeIsNumeric = (valueType?: PropertyValueType) => valueType === 'int' || valueType === 'float'

export const getBarcodeType = (property: AdvancedProperty) => {
  if (propertyCanGenerateEan(property) && 'customPath' in property) {
    return property.customPath?.[0] as BarcodeType
  }
}

// Specific product properties

export const getProductPropertyId = (product: SimplifiedProduct, property: AdvancedProperty) => `${product.sku}_${property.defaultHeader}`

export const productPropertyIsEditable = (partialProperty: PartialProperty, product?: SimplifiedProduct, productInput?: ProductInput) => {
  if (isSkuProperty(partialProperty)) {
    if (product && productInput) {
      return (
        product.hasTemporarySku ||
        !product.sku ||
        product.propertyStatuses?.sku === 'preliminary' ||
        productInput.propertyStatuses?.sku === 'preliminary' ||
        (productInput.sku !== undefined && product.sku !== productInput.sku)
      )
    }

    return false
  }

  return !partialProperty.readonly
}

export const productPropertyIsDeletable = (partialProperty: PartialProperty, product?: SimplifiedProduct, productInput?: ProductInput) => {
  if (!productPropertyIsEditable(partialProperty, product, productInput)) {
    return false
  }

  return !partialProperty.undeletable
}

export const productPropertyStatusIsEditable = (
  status: ProductPropertyStatus | null,
  partialProperty: PartialProperty,
  product?: SimplifiedProduct,
  productInput?: ProductInput,
) => {
  return isSkuProperty(partialProperty) || partialProperty.undeletable
    ? (status === 'preliminary' || status === null) && !isAvailableStatusProperty(partialProperty)
    : partialProperty.standardOptions !== undefined && status === 'checked'
      ? false
      : productPropertyIsEditable(partialProperty, product, productInput)
}

export const getTaxonomyFromProperty = (property: AdvancedProperty): RegisteredTaxonomy | undefined =>
  property.type === 'attribute'
    ? 'attributes'
    : property.type === 'categories'
      ? 'categories'
      : property.type === 'simpleTaxonomy'
        ? (property.defaultName.toLowerCase() as 'tags' | 'collections')
        : undefined

/** id or comparable defaultName */
export const getPropertyKey = (partialProperty: { defaultName: string; id?: string }) => partialProperty.id || toComparable(partialProperty.defaultName)
